<script setup lang="ts">
// Authenticated "add workspace" modal - the in-product counterpart to the
// public /signup funnel. An already-signed-in user creates a brand-new
// workspace owned by their session identity: POST /v1/api/me/workspaces
// (cookie-auth, invite-gated, atomic provisioning shared with /signup), then a
// full-page navigation into the new workspace's onboarding wizard.
//
// Still invite-gated during beta: an invite code is required and consumed
// single-use server-side, so a member can't spin up workspaces freely. The
// backend is authoritative; this form just collects name + invite + optional
// website and surfaces errors. Shell mirrors PromptModal so the two read as one
// system.

import AppIcon from '@/components/AppIcon.vue';
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';

import Modal from './Modal.vue';
import { api } from '@/api/client';
import { workspacePath } from '@/lib/workspace';
import { useI18n } from '@/lib/i18n';

const { t } = useI18n();

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ (e: 'close'): void }>();

const name = ref('');
const invite = ref('');
const website = ref('');
const pending = ref(false);
const error = ref<string | null>(null);
const nameEl = ref<HTMLInputElement | null>(null);

// One Idempotency-Key per form session: reused across submit retries (so a
// lost-response retry replays the same workspace instead of a spurious "invite
// already used"), regenerated when the modal reopens (a fresh create intent).
let idemKey = '';

// ── Slug preview (the same public endpoint /signup uses) ──
type SlugState = 'idle' | 'checking' | 'free' | 'taken' | 'hidden';
const slugState = ref<SlugState>('idle');
const slugPreview = ref('');
let slugTimer: ReturnType<typeof setTimeout> | undefined;

const canCreate = computed(
  () => name.value.trim() !== '' && invite.value.trim() !== '' && !pending.value,
);

watch(
  () => props.open,
  (o) => {
    if (!o) return;
    name.value = '';
    invite.value = '';
    website.value = '';
    error.value = null;
    slugState.value = 'idle';
    slugPreview.value = '';
    idemKey = crypto.randomUUID();
    void nextTick(() => nameEl.value?.focus());
  },
);

async function checkSlug(): Promise<void> {
  const n = name.value.trim();
  if (!n) {
    slugState.value = 'idle';
    slugPreview.value = '';
    return;
  }
  slugState.value = 'checking';
  try {
    const r = await api.get<{ slug: string; available: boolean }>(
      `/v1/auth/slug-available?name=${encodeURIComponent(n)}`,
    );
    if (name.value.trim() !== n) return; // a newer edit superseded this check
    slugPreview.value = r.slug;
    slugState.value = r.available ? 'free' : 'taken';
  } catch {
    if (name.value.trim() !== n) return;
    slugState.value = 'hidden';
    slugPreview.value = '';
  }
}

function onNameInput(): void {
  error.value = null;
  clearTimeout(slugTimer);
  if (!name.value.trim()) {
    slugState.value = 'idle';
    slugPreview.value = '';
    return;
  }
  slugState.value = 'checking';
  slugTimer = setTimeout(checkSlug, 400);
}

function close(): void {
  if (pending.value) return;
  emit('close');
}

async function submit(): Promise<void> {
  if (!canCreate.value) return;
  pending.value = true;
  error.value = null;
  try {
    const r = await api.post<{ slug: string }>(
      '/v1/api/me/workspaces',
      {
        workspace_name: name.value.trim(),
        invite_code: invite.value.trim(),
        website: website.value.trim() || undefined,
      },
      { 'Idempotency-Key': idemKey },
    );
    // Full-page navigation: re-bases the SPA router into the new workspace and
    // the freshly-minted session cookie scopes it. Land in the onboarding wizard.
    // Keep `pending` true so the button stays disabled through the navigation.
    window.location.assign(workspacePath(r.slug, '/onboarding'));
  } catch (e) {
    error.value = (e as { error?: string }).error ?? t('addWorkspace.err.create');
    pending.value = false;
  }
}

onBeforeUnmount(() => clearTimeout(slugTimer));
</script>

<template>
  <Modal :open="open" :title="t('addWorkspace.title')" :icon="'buildings'" :busy="pending" @close="close">
    <p class="lede">
      {{ t('addWorkspace.lede') }}
    </p>

    <p v-if="error" class="alert">
      <AppIcon name="warning" :size="14" />
      <span>{{ error }}</span>
    </p>

    <form class="form" @submit.prevent="submit">
      <label class="field">
        <span class="field__label">{{ t('addWorkspace.name_label') }}</span>
        <input
          ref="nameEl"
          v-model="name"
          type="text"
          class="field__input"
          autocomplete="organization"
          :placeholder="t('addWorkspace.name_placeholder')"
          @input="onNameInput"
        />
        <span v-if="slugState === 'checking'" class="hint">
          <AppIcon name="circle-notch" :size="13" class="hint__spin" />
          {{ t('addWorkspace.checking') }}
        </span>
        <span v-else-if="slugState === 'free'" class="hint hint--ok">
          <AppIcon name="check" :size="13" />
          {{ t('addWorkspace.slug_free_before') }} <strong>{{ slugPreview }}</strong>
        </span>
        <span v-else-if="slugState === 'taken'" class="hint hint--warn">
          <AppIcon name="warning" :size="13" />
          <strong>{{ slugPreview }}</strong> {{ t('addWorkspace.slug_taken_after') }}
        </span>
      </label>

      <label class="field">
        <span class="field__label">{{ t('addWorkspace.invite_label') }}</span>
        <input
          v-model="invite"
          type="text"
          class="field__input"
          autocomplete="off"
          spellcheck="false"
          :placeholder="t('addWorkspace.invite_placeholder')"
          @input="error = null"
        />
        <span class="hint">{{ t('addWorkspace.invite_hint') }}</span>
      </label>

      <label class="field">
        <span class="field__label">{{ t('addWorkspace.website_label') }} <em class="field__opt">{{ t('addWorkspace.optional') }}</em></span>
        <input
          v-model="website"
          type="text"
          class="field__input"
          autocomplete="url"
          placeholder="acme.com"
        />
      </label>
    </form>

    <template #footer>
      <button type="button" class="btn btn-secondary" :disabled="pending" @click="close">
        {{ t('common.cancel') }}
      </button>
      <button type="button" class="btn btn-primary" :disabled="!canCreate" @click="submit">
        <AppIcon name="circle-notch" v-if="pending" :size="15" class="hint__spin" />
        {{ pending ? t('addWorkspace.creating') : t('addWorkspace.create') }}
      </button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
@layer surface {
  .lede {
    margin: 0;
    color: var(--ink-muted);
    font-size: var(--text-sm);
    line-height: var(--leading-base);
  }

  .alert {
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
    margin: 0;
    padding: var(--space-3);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-button);
    background: var(--surface-sunken);
    color: var(--status-danger-text);
    font-size: var(--text-sm);
    line-height: 1.4;
  }
  .alert svg { flex-shrink: 0; margin-top: 1px; }

  .form { display: flex; flex-direction: column; gap: var(--space-4); }
  .field { display: flex; flex-direction: column; gap: var(--space-2); }
  .field__label { font-size: var(--text-xs); font-weight: 600; color: var(--ink-muted); }
  .field__opt { font-style: normal; font-weight: 400; color: var(--ink-subtle); }
  .field__input { width: 100%; }

  .hint {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-xs);
    color: var(--ink-subtle);
    line-height: 1.4;
  }
  .hint svg { flex-shrink: 0; }
  .hint--ok { color: var(--status-success-text); }
  .hint--warn { color: var(--status-warning-text, var(--ink-muted)); }
  .hint__spin { animation: awm-spin 900ms linear infinite; }
  @keyframes awm-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
}
</style>
